import type { SelectOption, TreeOption } from '@/components/organisms/Select/types';
import { buildDropdownOptions } from '@/components/templates/FilterBar/DefaultFiltersBar/helpers';

import { NEW_FILTERS_OPTIONS } from './const';

import type { CustomFilters, FilterValueSets, NewDefaultFiltersData, NewMultiFilterKey } from './types';

// hazardous and radius have static options, so other filters can't make their values unavailable
const FETCHED_FILTER_KEYS = [
  'countries',
  'disposers',
  'eurals',
  'locations',
  'materials',
  'processes',
  'processors',
  'projects',
] as const satisfies readonly NewMultiFilterKey[];

function collectOptionValues(options: ReadonlyArray<SelectOption | TreeOption>): string[] {
  return options.flatMap((option) => [
    String(option.value),
    ...('children' in option && option.children ? collectOptionValues(option.children) : []),
  ]);
}

/**
 * Maps freshly loaded filter data to the values each filter can select, using
 * the same option builders as the filters bar so tree values match.
 */
export function getAvailableFilterValues(
  defaultFilters: NewDefaultFiltersData,
  customFilters: CustomFilters
): FilterValueSets {
  const options = buildDropdownOptions(defaultFilters, NEW_FILTERS_OPTIONS);
  const available: FilterValueSets = {};

  for (const key of FETCHED_FILTER_KEYS) {
    // a missing response means availability is unknown, not that every value is unavailable
    if (defaultFilters[key] === undefined) continue;

    available[key] = new Set(collectOptionValues(options[key]));
  }

  for (const filter of Object.values(customFilters)) {
    available[filter.apiKey] = new Set(filter.items.map((item) => String(item.id)));
  }

  return available;
}

/**
 * Values that were selectable in the previous load but no longer are, so only
 * what the latest change made unavailable gets deselected.
 */
export function getNewlyUnavailableValues(previous: FilterValueSets, current: FilterValueSets): FilterValueSets {
  const unavailable: FilterValueSets = {};

  for (const [key, values] of Object.entries(current) as Array<[NewMultiFilterKey, ReadonlySet<string>]>) {
    const previousValues = previous[key];
    if (!previousValues) continue;

    unavailable[key] = new Set([...previousValues].filter((value) => !values.has(value)));
  }

  return unavailable;
}
